import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, ChevronLeft, ChevronRight, Lock, ShieldOff } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BranchFilter, useBranchFilter } from "@/admin/BranchFilter";
import { MonthView } from "@/admin/calendar/MonthView";
import { WeekView } from "@/admin/calendar/WeekView";
import { DayView, LockList } from "@/admin/calendar/DayView";
import { LockForm } from "@/admin/calendar/LockForm";
import { ManualBookingForm } from "@/admin/calendar/ManualBookingForm";
import { LEGEND, addDays, iso, monthTitle, overlapsDay, startOfWeek, toEvents, toneClass } from "@/admin/calendar/helpers";

const VIEWS = [["month", "Bulan"], ["week", "Minggu"], ["day", "Hari"]];
const KINDS = [["booking", "Booking"], ["hold", "Hold"], ["block", "Blokir"]];

export default function CalendarPage() {
  const { user } = useAuth();
  const [view, setView] = useState("month");
  const [anchor, setAnchor] = useState(new Date());
  const [data, setData] = useState({ bookings: [], locks: [], units: [], total_units: 0 });
  const [meta, setMeta] = useState({ lock_types: [] });
  const [kinds, setKinds] = useState(["booking", "hold", "block"]);
  const [unitId, setUnitId] = useState("");
  const [showCancelled, setShowCancelled] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [lockType, setLockType] = useState("admin_hold");
  const [formDate, setFormDate] = useState(iso(new Date()));
  const [convertHold, setConvertHold] = useState(null);
  const { branches, branchId, setBranchId } = useBranchFilter();

  const canWrite = ["owner", "admin"].includes(user?.role) || (user?.perms || []).includes("bookings:write");
  const isOwner = user?.role === "owner";

  const range = useCallback(() => {
    if (view === "day") return [iso(anchor), iso(anchor)];
    if (view === "week") {
      const s = startOfWeek(anchor);
      return [iso(s), iso(addDays(s, 6))];
    }
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = addDays(first, -((first.getDay() + 6) % 7));
    return [iso(gridStart), iso(addDays(gridStart, 41))];
  }, [view, anchor]);

  const [from, to] = range();

  const load = useCallback(() => {
    api.get("/calendar", { params: { date_from: from, date_to: to, branch_id: branchId || undefined } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Gagal memuat kalender"));
  }, [from, to, branchId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/calendar-lock-meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const events = useMemo(() => {
    let list = toEvents(data).filter((e) => kinds.includes(e.kind));
    if (!showCancelled) list = list.filter((e) => e.tone !== "cancelled");
    if (unitId) list = list.filter((e) => (e.units || []).includes(unitId));
    return list;
  }, [data, kinds, unitId, showCancelled]);

  const units = useMemo(
    () => (data.units || []).filter((u) => !unitId || u.id === unitId), [data.units, unitId]);
  const activeLocks = useMemo(
    () => (data.locks || []).filter((l) => l.status === "active" && !l.expired), [data.locks]);

  const step = (dir) => setAnchor((a) => {
    if (view === "day") return addDays(a, dir);
    if (view === "week") return addDays(a, dir * 7);
    return new Date(a.getFullYear(), a.getMonth() + dir, 1);
  });

  const openBooking = (date) => { setConvertHold(null); setFormDate(date || iso(anchor)); setBookingOpen(true); };
  const convert = (lock) => { setConvertHold(lock); setFormDate(lock.rental_date); setBookingOpen(true); };
  const openLock = (type) => { setLockType(type); setFormDate(iso(anchor)); setLockOpen(true); };
  const pickDay = (d) => { setAnchor(d); setView("day"); };

  const release = async (lock) => {
    if (!window.confirm(`Lepas ${lock.lock_type_label} "${lock.title}"? ${lock.quantity} meja jadi tersedia lagi.`)) return;
    try {
      const r = await api.post(`/calendar-locks/${lock.id}/release`, { reason: "Dilepas dari kalender" });
      toast.success(r.data.message || "Kunci dilepas");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal melepas kunci");
    }
  };

  const label = view === "month" ? monthTitle(anchor)
    : view === "week" ? `${from} → ${to}`
      : anchor.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div data-testid="admin-calendar">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl tracking-tight sm:text-4xl">Kalender Inventaris</h1>
          <p className="mt-1 max-w-xl text-sm text-ink-soft">
            Kalender website adalah satu-satunya sumber kebenaran. Booking, hold, dan blokir semuanya
            mengunci meja yang persis sama.
          </p>
        </div>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <button data-testid="cal-new-manual-booking" onClick={() => openBooking()}
              className="lm-btn-primary flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold">
              <CalendarPlus size={15} /> Booking manual
            </button>
            <button data-testid="cal-new-hold" onClick={() => openLock("admin_hold")}
              className="flex items-center gap-2 rounded-md border border-violet-400 bg-violet-50 px-4 py-2.5 text-sm font-semibold text-violet-900 transition-colors hover:bg-violet-100">
              <Lock size={15} /> Admin Hold
            </button>
            <button data-testid="cal-new-block" onClick={() => openLock("availability_block")}
              className="flex items-center gap-2 rounded-md border border-zinc-400 bg-zinc-100 px-4 py-2.5 text-sm font-semibold text-zinc-800 transition-colors hover:bg-zinc-200">
              <ShieldOff size={15} /> Blokir
            </button>
          </div>
        )}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <div className="flex rounded-md border border-black/15 bg-white p-0.5">
          {VIEWS.map(([v, l]) => (
            <button key={v} data-testid={`cal-view-${v}`} onClick={() => setView(v)}
              className={`rounded px-3 py-1.5 text-xs font-semibold transition-colors ${
                view === v ? "bg-ink text-cream" : "text-ink-soft hover:text-ink"}`}>{l}</button>
          ))}
        </div>
        <button data-testid="cal-prev" onClick={() => step(-1)}
          className="rounded-md border border-black/15 bg-white p-2 hover:border-ink"><ChevronLeft size={15} /></button>
        <button data-testid="cal-next" onClick={() => step(1)}
          className="rounded-md border border-black/15 bg-white p-2 hover:border-ink"><ChevronRight size={15} /></button>
        <button data-testid="cal-today" onClick={() => setAnchor(new Date())}
          className="rounded-md border border-black/15 bg-white px-3 py-1.5 text-xs">Hari ini</button>
        <span className="text-sm font-semibold" data-testid="cal-range-label">{label}</span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <BranchFilter branches={branches} value={branchId} onChange={setBranchId} testid="calendar-branch-filter" />
          <select data-testid="calendar-unit-filter" value={unitId} onChange={(e) => setUnitId(e.target.value)}
            className="h-10 rounded-md border border-input bg-white px-3 text-sm">
            <option value="">Semua meja</option>
            {(data.units || []).map((u) => <option key={u.id} value={u.id}>{u.unit_code}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex flex-wrap gap-1.5">
          {KINDS.map(([k, l]) => (
            <button key={k} data-testid={`cal-filter-${k}`}
              onClick={() => setKinds((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k])}
              className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${
                kinds.includes(k) ? "border-ink bg-ink text-cream" : "border-black/15 text-ink-soft"}`}>{l}</button>
          ))}
          <button data-testid="cal-filter-cancelled" onClick={() => setShowCancelled((v) => !v)}
            className={`rounded-full border px-3 py-1 text-[11px] ${showCancelled
              ? "border-ink bg-ink text-cream" : "border-black/15 text-ink-soft"}`}>Batal</button>
        </div>
        <div className="flex flex-wrap gap-2.5">
          {LEGEND.map((l) => (
            <span key={l.tone} className="flex items-center gap-1.5 text-[10px] text-ink-soft">
              <span className={`h-2.5 w-2.5 rounded-sm border ${toneClass(l.tone)}`} />{l.label}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-4">
        {view === "month" && (
          <MonthView anchor={anchor} events={events} totalUnits={data.total_units}
            onPickDay={pickDay} onQuickAdd={openBooking} />
        )}
        {view === "week" && <WeekView anchor={anchor} events={events} totalUnits={data.total_units} onPickDay={pickDay} />}
        {view === "day" && (
          <DayView dayIso={iso(anchor)} units={units}
            events={events.filter((e) => overlapsDay(e, iso(anchor)))} />
        )}
      </div>

      <LockList locks={activeLocks} onRelease={release} onConvert={convert} canEdit={canWrite} />

      {canWrite && (
        <>
          <ManualBookingForm open={bookingOpen} onOpenChange={setBookingOpen} branches={branches}
            defaultDate={formDate} defaultBranchId={branchId} isOwner={isOwner} hold={convertHold} onSaved={load} />
          <LockForm open={lockOpen} onOpenChange={setLockOpen} meta={meta} branches={branches}
            defaultDate={formDate} defaultType={lockType} defaultBranchId={branchId} onSaved={load} />
        </>
      )}
    </div>
  );
}
